import React from 'react';
import PropTypes from 'prop-types';
import { Filter, ChevronRight, X } from 'lucide-react';

// Category to Assessment Type mapping
const CATEGORY_ASSESSMENT_TYPE_MAP = {
  // Business Assessment categories
  financial_performance: 'business',
  revenue_quality: 'business',
  customer_concentration: 'business',
  management_team: 'business',
  competitive_position: 'business',
  growth_potential: 'business',
  intellectual_property: 'business',
  legal_compliance: 'business',
  owner_dependency: 'business',
  strategic_positioning: 'business',

  // Family Enterprise categories (part of business)
  family_governing: 'business',
  succession_planning: 'business',
  family_conflict: 'business',
  equity_planning: 'business',
  employment_policies: 'business',
  family_council: 'business',
  wealth_transfer: 'business',
  values_mission: 'business',
  education_development: 'business',
  boundary_management: 'business',

  // Personal Assessment - Financial
  personal_financial: 'personal',
  wealth_management: 'personal',
  estate_planning: 'personal',
  tax_planning: 'personal',
  insurance_risk: 'personal',
  advisor_relationships: 'personal',
  legacy_planning: 'personal',
  liquidity_cash: 'personal',
  family_communication: 'personal',
  lifestyle_resource: 'personal',

  // Personal Assessment - Wellbeing
  physical_health: 'personal',
  relational_health: 'personal',
  family_relationships: 'personal',
  emotional_mental: 'personal',
  purpose_identity: 'personal',
  time_management: 'personal',
  hobbies_interests: 'personal',
  social_connection: 'personal',
  exit_vision: 'personal',
  life_transition: 'personal',

  // Personal Assessment - Spiritual
  personal_spiritual: 'personal',
  biblical_wisdom: 'personal',
  marriage_family: 'personal',
  business_stewardship: 'personal',
  character_integrity: 'personal',
  eternal_impact: 'personal',
};

/**
 * TaskFilters component - Collapsible filters panel for tasks
 */
const TaskFilters = ({
  showFilters,
  setShowFilters,
  filterValues,
  onFilterChange,
  assessmentQuestions,
  onResetFilters,
}) => {
  const {
    filter,
    priorityFilter,
    activityTypeFilter,
    intangibleCapitalFilter,
    dueDateFilter,
    gapScoreFilter,
    filterCategory,
    filterQuestionId,
    assessmentTypeFilter,
    sortBy,
    sortOrder,
  } = filterValues;

  // Helper function to check if a category should be shown based on assessment type filter
  const shouldShowCategory = (category) => {
    if (assessmentTypeFilter === 'all') return true;
    return CATEGORY_ASSESSMENT_TYPE_MAP[category] === assessmentTypeFilter;
  };

  const hasActiveFilters = filter !== 'all' ||
    priorityFilter !== 'all' ||
    activityTypeFilter !== 'all' ||
    intangibleCapitalFilter !== 'all' ||
    dueDateFilter !== 'all' ||
    gapScoreFilter !== 'all' ||
    filterCategory !== '';

  return (
    <div className="bg-white rounded-xl shadow-lg mb-6 overflow-hidden">
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition"
      >
        <div className="flex items-center gap-3">
          <Filter size={22} className="text-blue-600" />
          <span className="font-bold text-gray-900 text-lg">Filters</span>
          {hasActiveFilters && (
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full">Active</span>
          )}
        </div>
        <ChevronRight
          size={20}
          className={`text-gray-600 transition-transform ${showFilters ? 'rotate-90' : ''}`}
        />
      </button>

      {showFilters && (
        <div className="p-6 space-y-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
              <select
                value={filter}
                onChange={(e) => onFilterChange('filter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Statuses</option>
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="under_review">Under Review</option>
                <option value="completed">Completed</option>
                <option value="not_relevant">Not Relevant</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => onFilterChange('priorityFilter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Priorities</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            {/* Activity Type Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Activity Type</label>
              <select
                value={activityTypeFilter}
                onChange={(e) => onFilterChange('activityTypeFilter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Activities</option>
                <option value="De-Risking Activity">De-Risking Activity</option>
                <option value="Strategy Activity">Strategy Activity</option>
                <option value="Efficiency Activity">Efficiency Activity</option>
                <option value="Growth Activity">Growth Activity</option>
                <option value="Culture Activity">Culture Activity</option>
              </select>
            </div>

            {/* Intangible Asset Type Filter - Only show for Business Gap Assessment */}
            {(assessmentTypeFilter === 'all' || assessmentTypeFilter === 'business') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Intangible Type</label>
                <select
                  value={intangibleCapitalFilter}
                  onChange={(e) => onFilterChange('intangibleCapitalFilter', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="Human">Human</option>
                  <option value="Structural">Structural</option>
                  <option value="Customer">Customer</option>
                  <option value="Social">Social</option>
                  <option value="Revenue & Profitability">Revenue & Profitability</option>
                </select>
              </div>
            )}

            {/* Due Date Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Due Date</label>
              <select
                value={dueDateFilter}
                onChange={(e) => onFilterChange('dueDateFilter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Dates</option>
                <option value="overdue">Overdue</option>
                <option value="today">Today</option>
                <option value="next_7_days">Next 7 Days</option>
                <option value="next_30_days">Next 30 Days</option>
                <option value="next_90_days">Next 90 Days</option>
                <option value="beyond_90_days">Beyond 90 Days</option>
                <option value="no_date">No Date Set</option>
              </select>
            </div>

            {/* Gap Score Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Gap Score</label>
              <select
                value={gapScoreFilter}
                onChange={(e) => onFilterChange('gapScoreFilter', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Gap Scores</option>
                <option value="no_gaps">No Gaps (&gt;86%)</option>
                <option value="minor_gaps">Minor Gaps (72-86%)</option>
                <option value="considerable_gaps">Considerable Gaps (57-72%)</option>
                <option value="critical_gaps">Critical Gaps (43-57%)</option>
                <option value="very_critical_gaps">Very Critical Gaps (28-43%)</option>
                <option value="extremely_critical">Extremely Critical (0-28%)</option>
              </select>
            </div>

            {/* Assessment Type Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Assessment Type</label>
              <select
                value={assessmentTypeFilter}
                onChange={(e) => {
                  const newValue = e.target.value;
                  onFilterChange('assessmentTypeFilter', newValue);
                  onFilterChange('filterCategory', ''); // Reset category when assessment type changes
                  onFilterChange('filterQuestionId', ''); // Reset question when assessment type changes
                  // Reset intangible capital filter when switching to personal (since personal questions don't have this field)
                  if (newValue === 'personal') {
                    onFilterChange('intangibleCapitalFilter', 'all');
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Assessments</option>
                <option value="business">Business Gap Assessment</option>
                <option value="personal">Personal Gap Assessment</option>
              </select>
            </div>

            {/* Question Category Filter */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Question Category</label>
              <select
                value={filterCategory}
                onChange={(e) => {
                  onFilterChange('filterCategory', e.target.value);
                  onFilterChange('filterQuestionId', ''); // Reset question when category changes
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {(assessmentTypeFilter === 'all' || assessmentTypeFilter === 'business') && (
                  <>
                    <optgroup label="Business Assessment">
                      {shouldShowCategory('financial_performance') && <option value="financial_performance">Financial Performance & Documentation</option>}
                      {shouldShowCategory('revenue_quality') && <option value="revenue_quality">Revenue Quality & Predictability</option>}
                      {shouldShowCategory('customer_concentration') && <option value="customer_concentration">Customer Concentration & Relationships</option>}
                      {shouldShowCategory('management_team') && <option value="management_team">Management Team & Operations</option>}
                      {shouldShowCategory('competitive_position') && <option value="competitive_position">Competitive Position & Market Dynamic</option>}
                      {shouldShowCategory('growth_potential') && <option value="growth_potential">Growth Potential & Scalability</option>}
                      {shouldShowCategory('intellectual_property') && <option value="intellectual_property">Intellectual Property & Proprietary Assets</option>}
                      {shouldShowCategory('legal_compliance') && <option value="legal_compliance">Legal, Compliance & Risk Management</option>}
                      {shouldShowCategory('owner_dependency') && <option value="owner_dependency">Owner Dependency & Transferability</option>}
                      {shouldShowCategory('strategic_positioning') && <option value="strategic_positioning">Strategic Positioning & Market Reputation</option>}
                    </optgroup>
                    <optgroup label="Family Enterprise">
                      {shouldShowCategory('family_governing') && <option value="family_governing">Family Governance & Decision-Making Structure</option>}
                      {shouldShowCategory('succession_planning') && <option value="succession_planning">Succession Planning & Next-Generation Readiness</option>}
                      {shouldShowCategory('family_conflict') && <option value="family_conflict">Family Communication & Conflict Resolution</option>}
                      {shouldShowCategory('equity_planning') && <option value="equity_planning">Ownership Structure & Equity Planning</option>}
                      {shouldShowCategory('employment_policies') && <option value="employment_policies">Family Employment & Compensation Policies</option>}
                      {shouldShowCategory('family_council') && <option value="family_council">Family Council & Board Effectiveness</option>}
                      {shouldShowCategory('wealth_transfer') && <option value="wealth_transfer">Wealth Transfer & Estate Integration</option>}
                      {shouldShowCategory('values_mission') && <option value="values_mission">Family Values, Mission & Legacy Preservation</option>}
                      {shouldShowCategory('education_development') && <option value="education_development">Education & Development of Rising Generation</option>}
                      {shouldShowCategory('boundary_management') && <option value="boundary_management">Family-Business Boundary Management</option>}
                    </optgroup>
                  </>
                )}
                {(assessmentTypeFilter === 'all' || assessmentTypeFilter === 'personal') && (
                  <>
                    <optgroup label="Personal Assessment - Financial">
                      {shouldShowCategory('personal_financial') && <option value="personal_financial">Personal Financial Goals & Planning</option>}
                      {shouldShowCategory('wealth_management') && <option value="wealth_management">Wealth Management & Investment Strategy</option>}
                      {shouldShowCategory('estate_planning') && <option value="estate_planning">Estate Planning & Document Structure</option>}
                      {shouldShowCategory('tax_planning') && <option value="tax_planning">Tax Planning & Efficiency</option>}
                      {shouldShowCategory('insurance_risk') && <option value="insurance_risk">Insurance & Risk Protection</option>}
                      {shouldShowCategory('advisor_relationships') && <option value="advisor_relationships">Advisory Team & Professional Relationships</option>}
                      {shouldShowCategory('legacy_planning') && <option value="legacy_planning">Legacy Planning & Charitable Intent</option>}
                      {shouldShowCategory('liquidity_cash') && <option value="liquidity_cash">Liquidity & Cash Flow Management</option>}
                      {shouldShowCategory('family_communication') && <option value="family_communication">Family Financial Governance & Communication</option>}
                      {shouldShowCategory('lifestyle_resource') && <option value="lifestyle_resource">Post-Exit Lifestyle & Resource Alignment</option>}
                    </optgroup>
                    <optgroup label="Personal Assessment - Wellbeing">
                      {shouldShowCategory('physical_health') && <option value="physical_health">Physical Health & Vitality</option>}
                      {shouldShowCategory('relational_health') && <option value="relational_health">Relational Health & Marriage</option>}
                      {shouldShowCategory('family_relationships') && <option value="family_relationships">Family Relationships & Legacy</option>}
                      {shouldShowCategory('emotional_mental') && <option value="emotional_mental">Emotional & Mental Wellbeing</option>}
                      {shouldShowCategory('purpose_identity') && <option value="purpose_identity">Purpose & Identity Beyond Business</option>}
                      {shouldShowCategory('time_management') && <option value="time_management">Time Management & Rhythm</option>}
                      {shouldShowCategory('hobbies_interests') && <option value="hobbies_interests">Hobbies, Interests & Personal Growth</option>}
                      {shouldShowCategory('social_connection') && <option value="social_connection">Social Connection & Community</option>}
                      {shouldShowCategory('exit_vision') && <option value="exit_vision">Post-Exit Vision & Planning</option>}
                      {shouldShowCategory('life_transition') && <option value="life_transition">Life Transitions & Change Readiness</option>}
                    </optgroup>
                    <optgroup label="Personal Assessment - Spiritual">
                      {shouldShowCategory('personal_spiritual') && <option value="personal_spiritual">Personal Spiritual Foundation</option>}
                      {shouldShowCategory('biblical_wisdom') && <option value="biblical_wisdom">Biblical Wisdom & Decision-Making</option>}
                      {shouldShowCategory('marriage_family') && <option value="marriage_family">Marriage & Family Spiritual Leadership</option>}
                      {shouldShowCategory('business_stewardship') && <option value="business_stewardship">Business as Ministry & Stewardship</option>}
                      {shouldShowCategory('character_integrity') && <option value="character_integrity">Character, Integrity & Health</option>}
                      {shouldShowCategory('eternal_impact') && <option value="eternal_impact">Eternal Impact & Crisis Readiness</option>}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {/* Specific Question Filter - Only shown when category is selected */}
            {filterCategory && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Specific Question</label>
                <select
                  value={filterQuestionId}
                  onChange={(e) => onFilterChange('filterQuestionId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Questions in Category</option>
                  {assessmentQuestions
                    .filter(q => q.category === filterCategory)
                    .map((q) => (
                      <option key={q.question_id} value={q.question_id}>
                        {q.question_id}: {q.subject}
                      </option>
                    ))}
                </select>
              </div>
            )}
          </div>

          {/* Sort Section */}
          <div className="border-t pt-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => onFilterChange('sortBy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="created_at">Created Date</option>
                  <option value="title">Title</option>
                  <option value="priority">Priority</option>
                  <option value="status">Status</option>
                  <option value="due_date">Due Date</option>
                  <option value="gap_score">Gap Score</option>
                  <option value="category">Category</option>
                  <option value="activity_type">Activity Type</option>
                  <option value="intangible_asset_type">Intangible Asset Type</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Sort Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => onFilterChange('sortOrder', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="desc">Descending (↓)</option>
                  <option value="asc">Ascending (↑)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reset Filters Button */}
          <div className="border-t pt-4 mt-4">
            <button
              onClick={onResetFilters}
              className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition flex items-center justify-center gap-2"
            >
              <X size={18} />
              Reset All Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TaskFilters.propTypes = {
  showFilters: PropTypes.bool.isRequired,
  setShowFilters: PropTypes.func.isRequired,
  filterValues: PropTypes.shape({
    filter: PropTypes.string.isRequired,
    priorityFilter: PropTypes.string.isRequired,
    activityTypeFilter: PropTypes.string.isRequired,
    intangibleCapitalFilter: PropTypes.string.isRequired,
    dueDateFilter: PropTypes.string.isRequired,
    gapScoreFilter: PropTypes.string.isRequired,
    filterCategory: PropTypes.string.isRequired,
    filterQuestionId: PropTypes.string.isRequired,
    sortBy: PropTypes.string.isRequired,
    sortOrder: PropTypes.string.isRequired,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  assessmentQuestions: PropTypes.arrayOf(PropTypes.shape({
    question_id: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    subject: PropTypes.string.isRequired,
  })).isRequired,
  onResetFilters: PropTypes.func.isRequired,
};

export default TaskFilters;
